import styled, { keyframes } from 'styled-components';

const slideInUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(calc(100% + 24px));
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const slideOutDown = keyframes`
  from {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
  to {
    opacity: 0;
    transform: translateY(calc(100% + 16px)) scale(0.98);
  }
`;

export const Overlay = styled.aside<{ $visible: boolean; $exiting: boolean }>`
  position: fixed;
  inset-inline: 0;
  bottom: 0;
  z-index: 60;
  direction: rtl;
  padding: 0 20px max(20px, env(safe-area-inset-bottom));
  pointer-events: ${(p) => (p.$visible && !p.$exiting ? 'auto' : 'none')};
  animation: ${(p) => {
    if (p.$exiting) return slideOutDown;
    if (p.$visible) return slideInUp;
    return 'none';
  }}
    0.5s cubic-bezier(0.22, 1, 0.36, 1) both;

  @media (max-width: 768px) {
    padding: 0 16px max(16px, env(safe-area-inset-bottom));
  }

  @media (prefers-reduced-motion: reduce) {
    animation: none;
    opacity: ${(p) => (p.$visible && !p.$exiting ? 1 : 0)};
    transform: none;
  }
`;

export const Banner = styled.div`
  position: relative;
  max-width: 1200px;
  margin: 0 auto;
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 18px 20px 18px 20px;
  border-radius: 16px;
  background: oklch(0.2078 0.0207 261.5 / 0.92);
  backdrop-filter: blur(16px);
  -webkit-backdrop-filter: blur(16px);
  border: 1px solid oklch(1 0 0 / 0.09);
  box-shadow:
    0 8px 32px oklch(0.1 0.02 261.5 / 0.32),
    0 2px 8px oklch(0.1 0.02 261.5 / 0.2),
    inset 0 1px 0 oklch(1 0 0 / 0.07);
  box-sizing: border-box;
  font-family: var(--font-family, 'SuisseIntl', system-ui, sans-serif);
  @media (max-width: 768px) {
    flex-direction: column;
    align-items: stretch;
    gap: 14px;
    padding: 16px;
    border-radius: 14px;
  }
`;

export const Content = styled.div`
  flex: 1;
  min-width: 0;
`;

export const IconWrap = styled.span`
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border-radius: 10px;
  background: oklch(0.65 0.24 33.09 / 0.18);
  color: var(--primary-color, oklch(0.72 0.19 33.09));
  box-shadow: inset 0 0 0 1px oklch(0.65 0.24 33.09 / 0.22);
`;

export const TitleRow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 6px;
`;

export const Title = styled.p`
  margin: 0;
  font-size: 14px;
  font-weight: 700;
  line-height: 1.35;
  color: oklch(0.9784 0.0034 247.86);
  letter-spacing: -0.01em;
`;

export const Message = styled.p`
  margin: 0;
  font-size: 12px;
  font-weight: 400;
  line-height: 1.55;
  color: oklch(0.7686 0.0184 258.4);
`;

export const Actions = styled.div`
  flex-shrink: 0;
  display: flex;
  align-items: center;
  gap: 6px;
  direction: ltr;

  @media (max-width: 768px) {
    width: 100%;
  }
`;

const actionButtonBase = `
  min-width: 76px;
  padding: 8px 18px;
  border-radius: 999px;
  font-family: inherit;
  font-size: 12px;
  font-weight: 700;
  cursor: pointer;
  white-space: nowrap;
  transition:
    transform 0.2s cubic-bezier(0.45, -0.32, 0.13, 1.32),
    background-color 0.2s ease,
    opacity 0.2s ease;

  &:active {
    transform: scale(0.97);
  }

  &:focus-visible {
    outline: 2px solid var(--primary-color, oklch(0.65 0.24 33.09));
    outline-offset: 3px;
  }

  @media (max-width: 768px) {
    flex: 1;
    min-width: unset;
    padding: 9px 14px;
  }
`;

export const AcceptButton = styled.button`
  ${actionButtonBase}
  border: none;
  background-color: var(--primary-color, oklch(0.65 0.24 33.09));
  color: var(--btn-text-active, oklch(1 0 0));

  &:hover {
    opacity: 0.95;
  }
`;

export const RejectButton = styled.button`
  ${actionButtonBase}
  border: none;
  background-color: oklch(1 0 0 / 0.1);
  color: oklch(0.9219 0.0034 247.86);

  &:hover {
    background-color: oklch(1 0 0 / 0.16);
  }
`;
